import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { useEntity } from '../contexts/EntityContext';
import { extractPlatformUser } from '../utils/auth0UserUtils';
import { fetchAllEntitledEntities, fetchUserInfo } from '../services/entityApi';
import type { Entity } from '../types';
import { useFeatureFlag, FeatureFlagKey } from './useFeatureFlags';
import { useLoggerWithContext } from '@/services/logger/loggerContext';

/**
 * Minimal type for the Segment analytics.js global.
 * Only the methods we actually call are declared.
 */
interface SegmentAnalytics {
  identify: (userId: string, traits: Record<string, unknown>) => void;
  group: (groupId: string, traits: Record<string, unknown>) => void;
}

function getAnalytics(): SegmentAnalytics | undefined {
  return (window as unknown as { analytics?: SegmentAnalytics }).analytics;
}

const TWO_MINUTES = 2 * 60 * 1000;
const ONE_HOUR = 60 * 60 * 1000;
const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;

/**
 * Builds the visitor traits object for `analytics.identify()`.
 *
 * Sources:
 * - Auth0 user object: email, name, given_name, family_name
 * - Auth0 custom claims: platform_user.uuid, platform_user.delphius_role
 * - EntityContext: activeEntity.id (og_entity_id — the UUID)
 * - User API v2 (GET /api/v2/users/{uuid}): department, role.display_name
 *
 * Intentionally omitted fields:
 * - ogp_role: Legacy "OpenGov Prod" role — no source exists in PSP or Access Control.
 */
export function buildVisitorTraits(
  user: { email?: string; name?: string; given_name?: string; family_name?: string },
  delphiusRole: string | undefined,
  /**
   * The entity **UUID**, not the slug.
   *
   * `og_entity_id` is the stable identifier and matches the group key passed
   * to `analytics.group(activeEntity.id, …)`. The readable slug lives in
   * `og_subdomain` on the account. Before this change both carried the slug,
   * so the two traits were duplicates and the one named `_id` could not be
   * joined on.
   */
  entityUuid: string | undefined,
  /** The readable slug, carried on the person as well as the account so a
   *  person row is legible without joining the group. */
  entitySlug: string | undefined,
  department: string | undefined,
  roleDisplayName: string | undefined
): Record<string, unknown> {
  return {
    og_email: user.email,
    og_full_name: user.name,
    og_first_name: user.given_name,
    og_last_name: user.family_name,
    // Often null — most customers don't populate department in user profiles.
    og_department: department,
    og_entity_id: entityUuid,
    og_subdomain: entitySlug,
    og_internal: user.email?.endsWith('@opengov.com') ?? false,
    // Raw Auth0 claim value (e.g., "entity_admin", "entity_user", "opengov_admin").
    // Note: the entity_admin role may be deprecated in a future platform change;
    // this field gracefully handles null/undefined if the claim is absent.
    psp_role: delphiusRole,
    // Human-readable role from User API (e.g., "System Administrator", "Standard User").
    // Provides a friendlier label for Pendo segmentation alongside the raw psp_role.
    psp_role_display: roleDisplayName,
  };
}

/**
 * Builds the account traits object for `analytics.group()`.
 *
 * Sources:
 * - EntityContext: activeEntity (id, name, friendlyId)
 * - EntityContext: entitledApplications (product display names)
 * - Full Entity (fetched separately): address, thirdPartyIds, entityType
 *
 * Intentionally omitted fields:
 * - og_planLevel: Only available in Salesforce — not accessible from PSP APIs.
 * - og_domain: Government domain (e.g., "springfield.gov") — Salesforce-only field.
 *   Both are populated by Pendo's native Salesforce integration instead.
 */
export function buildAccountTraits(
  entityName: string,
  entityFriendlyId: string,
  entitledProducts: string,
  fullEntity: Entity | null | undefined
): Record<string, unknown> {
  return {
    og_account_name: entityName,
    og_products: entitledProducts,
    og_subdomain: entityFriendlyId,
    og_country: fullEntity?.address?.country,
    og_region: fullEntity?.address?.region,
    // City depends on the Entity API address object. Many entities lack a discrete
    // city field (it may be embedded in the street string). No parsing of street
    // to infer city is attempted — this field is null when not explicitly present.
    og_city: fullEntity?.address?.city,
    og_salesforceid: fullEntity?.thirdPartyIds?.salesforceId,
  };
}

/**
 * Calls `analytics.identify()` and `analytics.group()` with visitor and
 * account metadata sourced from Auth0, EntityContext, the User API, and
 * the full (un-stripped) Entity API response.
 *
 * This hook is the canonical Segment identity layer for PSP. All sub-apps
 * running inside the platform shell inherit this identity via Segment's
 * downstream integrations (Pendo, etc.). Sub-apps must NOT re-initialize
 * Pendo — they should call `pendo.updateOptions()` instead.
 *
 * Must be rendered inside `<EntityProvider>` (needs entity context) and
 * `<ProtectedRoute>` (needs Auth0 session).
 *
 * @see pendo-research.md for field-by-field source mapping
 */
export function useSegmentIdentify(): void {
  const { user, getAccessTokenSilently } = useAuth();
  const { activeEntity, activeEntityEntitledApplications } = useEntity();
  const segmentAnalyticsEnabled = useFeatureFlag(FeatureFlagKey.PSP_ANALYTICS_SEGMENT);
  const logger = useLoggerWithContext();

  const platformUser = extractPlatformUser(user);
  const userUuid = platformUser?.uuid;

  // Track the last identify/group fingerprint to avoid redundant calls
  const lastIdentifyRef = useRef<string>('');

  // ── Full entity data (address, salesforceId) ──────────────────────
  // EntityContext strips entities down to EntityBase via extractBaseEntity().
  // This parallel query hits the same API endpoint (HTTP-cached) but preserves
  // the full response so analytics can access address and thirdPartyIds.
  const { data: fullEntity } = useQuery({
    queryKey: ['analyticsFullEntity', userUuid, activeEntity?.friendlyId],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const entities = await fetchAllEntitledEntities(userUuid!, token, logger);
      return entities.find((e) => e.friendlyId === activeEntity?.friendlyId) ?? null;
    },
    enabled: !!userUuid && !!activeEntity && segmentAnalyticsEnabled,
    staleTime: TWO_MINUTES,
    gcTime: TWENTY_FOUR_HOURS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // ── User info (department, role) ──────────────────────────────────
  // The Control Panel User API v2 returns department and role but PSP's
  // existing useUserDisplayName hook only extracts full_name. This query
  // fetches the full UserInfo including department and role for analytics.
  const { data: userInfo } = useQuery({
    queryKey: ['user-info', userUuid],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return fetchUserInfo(userUuid!, token, logger);
    },
    enabled: !!userUuid && segmentAnalyticsEnabled,
    staleTime: ONE_HOUR,
    gcTime: ONE_HOUR,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // ── Dispatch identify + group ─────────────────────────────────────
  // Fires when auth, entity, and supplemental data are all available.
  // Re-fires on entity switch (activeEntity change) so downstream
  // services like Pendo always reflect the current account context.
  useEffect(() => {
    if (!segmentAnalyticsEnabled) return;
    if (!userUuid || !activeEntity || !user) return;

    const analytics = getAnalytics();
    if (!analytics) return;

    // Deduplicate: skip if we've already identified this exact user+entity+data combo
    const fingerprint = `${userUuid}:${activeEntity.friendlyId}:${userInfo?.department ?? ''}:${userInfo?.role?.display_name ?? ''}:${fullEntity?.address?.country ?? ''}`;
    if (lastIdentifyRef.current === fingerprint) return;
    lastIdentifyRef.current = fingerprint;
    const visitorTraits = buildVisitorTraits(
      user,
      platformUser?.delphius_role,
      activeEntity.id,
      activeEntity.friendlyId,
      userInfo?.department,
      userInfo?.role?.display_name
    );

    const entitledProducts = activeEntityEntitledApplications
      .map((app) => app.displayName)
      .join(', ');

    const accountTraits = buildAccountTraits(
      activeEntity.name,
      activeEntity.friendlyId,
      entitledProducts,
      fullEntity
    );

    analytics.identify(userUuid, visitorTraits);
    analytics.group(activeEntity.id, accountTraits);
  }, [
    userUuid,
    user,
    platformUser?.delphius_role,
    activeEntity,
    activeEntityEntitledApplications,
    userInfo,
    fullEntity,
    segmentAnalyticsEnabled,
  ]);
}
